import { type FC, useCallback, useEffect } from 'react';
import { type IIdleTimerProps, useIdleTimer } from 'react-idle-timer';
import styled from 'styled-components';
import { useUserInfo } from '../auth';
import { TextLabel } from '../common/components/TextLabel';
import { Modal } from '../common/components';
import { formatTime } from '../common/utils';
import { useHLock } from './useHLock';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './HLock.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory('edit-hlock');
addTranslations(translations);

const IDLE_TIMEOUT_MINUTES = 17 * 60_000;
const PROMPT_BEFORE_IDLE_MINUTES = 2 * 60_000;

export interface HLockProps {
  path: string[];
  onLocked: () => void;
}

export const HLock: FC<HLockProps> = (props) => {
  const { path, onLocked } = props;
  const { t } = useTranslation();
  const hLock = useHLock(path);

  useEffect(() => {
    if (hLock) {
      Modal.error({
        title: t('modal.error.title'),
        content: t('modal.error.content', {
          username: hLock.username ?? '?',
        }),
        onOk: () => {
          Modal.destroyAll();
          onLocked();
        },
      });
    }
  }, [hLock, onLocked, t]);

  const onIdle = useCallback(() => {
    Modal.destroyAll();
    onLocked();
  }, [onLocked]);

  const onPrompt: Required<IIdleTimerProps>['onPrompt'] = useCallback(
    (event, idleTimer) => {
      Modal.confirm({
        title: t('title'),
        okText: t('button.continue'),
        cancelText: t('button.release'),
        content: <ContentWrapper>{t('label.object-locked')}</ContentWrapper>,
        onCancel: () => {
          onLocked();
        },
        onOk: () => {
          idleTimer?.reset();
        },
        width: '30em',
      });
    },
    [onLocked, t],
  );

  useIdleTimer({
    onIdle,
    onPrompt,
    promptBeforeIdle: PROMPT_BEFORE_IDLE_MINUTES,
    timeout: IDLE_TIMEOUT_MINUTES,
    throttle: 500,
    disabled: Boolean(hLock),
  });

  const { user: sessionUser } = useUserInfo();

  const username = hLock ? hLock.username : sessionUser.username;
  const until = hLock ? formatTime(hLock.ts) : undefined;
  const context = hLock ? undefined : 'yourself';

  return (
    <TextLabel value={t('locked-by-user', { username, until, context })} />
  );
};

const ContentWrapper = styled.span`
  white-space: pre-wrap;
`;
